import { useMutation } from '@apollo/client';
import {
  Button,
  ControlledTextArea,
  InputFormContainer,
  ModalActions,
  ModalHeader,
  Stack,
  Typography,
  colors,
  ControlledInput,
} from '@ui-components/neo.tax';
import { ReactComponent as RewardStarIcon } from '@ui-components/neo.tax/dist/icons/reward-star-3.svg';
import React, { useEffect, useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import {
  COMPANY_ELEVATOR_PITCH_HELP_TEXT,
  COMPANY_ELEVATOR_PITCH_PLACEHOLDER,
  GENERATING_ELEVATOR_PITCH_TEXTS,
} from '@neo.tax/shared/constants';
import {
  GENERATE_ELEVATOR_PITCH,
  GenerateElevatorPitchMutation,
  GenerateElevatorPitchMutationVariables,
} from '@neo.tax/shared/graphql';
import {
  useCompanyInformation,
  useUpdateCompanyQuestion,
  useCurrentCompanyId,
  useAlternatingText,
} from '@neo.tax/shared/hooks';
import { isUrl } from '@neo.tax/shared/utils';

import { BuildingModernIcon, Modal } from './get-started-hero.styled';
import { useCompanyInformationModalAnalyticIds } from './hooks';

type CompanyInfoModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type CompanyInfoFormType = {
  elevatorPitch: string;
  companyUrl: string;
};

const GENERATE_ELEVATOR_PITCH_REQUEST_KEY = 'GENERATE_ELEVATOR_PITCH_REQUEST_KEY';

export const CompanyInfoModal = ({ isOpen, onClose }: CompanyInfoModalProps): React.ReactElement => {
  const { companyUrlAnalyticId, elevatorPitchAnalyticId } = useCompanyInformationModalAnalyticIds();
  const {
    companyInformation: {
      companyUrl,
      companyUrlId,
      companyUrlQuestionId,
      elevatorPitch,
      elevatorPitchId,
      elevatorPitchQuestionId,
    },
  } = useCompanyInformation();

  const companyId = useCurrentCompanyId();

  const updateElevatorPitch = useUpdateCompanyQuestion(elevatorPitchId ?? '', elevatorPitchQuestionId ?? '');
  const updateCompanyUrl = useUpdateCompanyQuestion(companyUrlId ?? '', companyUrlQuestionId ?? '');

  const { control, handleSubmit, reset, setValue, watch } = useForm<CompanyInfoFormType>({
    mode: 'onSubmit',
    defaultValues: {
      elevatorPitch: elevatorPitch ?? '',
      companyUrl: companyUrl ? companyUrl.replace(/^https?:\/\//, '') : '',
    },
  });

  const watchCompanyUrl = watch('companyUrl');
  const formattedCompanyUrl = watchCompanyUrl ? `https://${watchCompanyUrl}` : '';
  const isCompanyUrlValid = useMemo(() => formattedCompanyUrl && isUrl(formattedCompanyUrl), [formattedCompanyUrl]);

  useEffect(() => {
    if (isCompanyUrlValid && companyUrl !== formattedCompanyUrl) {
      updateCompanyUrl({ answerValues: formattedCompanyUrl });
    }
  }, [companyUrl, formattedCompanyUrl, isCompanyUrlValid, updateCompanyUrl]);

  const [generateElevatorPitch, { loading: isGeneratingElevatorPitch }] = useMutation<
    GenerateElevatorPitchMutation,
    GenerateElevatorPitchMutationVariables
  >(GENERATE_ELEVATOR_PITCH, {
    context: {
      serializationKey: GENERATE_ELEVATOR_PITCH_REQUEST_KEY,
      debounceKey: GENERATE_ELEVATOR_PITCH_REQUEST_KEY,
      errorToast: {
        title: 'Error',
        description: 'Failed to generate elevator pitch',
        duration: 3000,
      },
      successToast: {
        title: 'Success',
        description: 'Elevator pitch generated',
        duration: 3000,
      },
      refetchQueries: ['GetQuestionSections'],
    },
  });

  const generatingButtonText = useAlternatingText(GENERATING_ELEVATOR_PITCH_TEXTS, 3000, isGeneratingElevatorPitch);

  const handleCancel = (): void => {
    reset();
    onClose();
  };

  const onSubmit: SubmitHandler<CompanyInfoFormType> = async (data) => {
    await updateElevatorPitch({ answerValues: data.elevatorPitch });
    onClose();
  };

  const handleAutogenerate = async (): Promise<void> => {
    const { data } = await generateElevatorPitch({
      variables: {
        companyId,
      },
    });
    setValue('elevatorPitch', data?.generateElevatorPitch.answerValue ?? '');
  };

  return (
    <Modal isOpen={isOpen}>
      <Stack as="form" direction="column" space={8} onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader icon={<BuildingModernIcon />} iconBgColor={colors.white} title="Company Information" />

        <ControlledInput
          addOn="https://"
          label="Company URL"
          key="companyUrl"
          control={control}
          name="companyUrl"
          id={companyUrlAnalyticId}
          placeholder="www.example.com"
          rules={{
            required: 'Please enter the company URL',
          }}
        />
        <InputFormContainer
          label="Your company's elevator pitch is..."
          helpText={COMPANY_ELEVATOR_PITCH_HELP_TEXT}
          inputId="elevatorPitch"
          action={
            <Stack direction="column" alignItems="end">
              <Typography fontSize="xs" color={colors.gray[500]}>
                Please fill in the Company URL to enable auto-generation.
              </Typography>
              <Button
                variant="secondary"
                rounded="regular"
                size="xs"
                onClick={handleAutogenerate}
                type="button"
                disabled={isGeneratingElevatorPitch || !isCompanyUrlValid}
                startIcon={<RewardStarIcon width={14} />}
              >
                {isGeneratingElevatorPitch ? generatingButtonText : 'Auto-generate from Company URL'}
              </Button>
            </Stack>
          }
        >
          <ControlledTextArea
            control={control}
            name="elevatorPitch"
            id={elevatorPitchAnalyticId}
            placeholder={COMPANY_ELEVATOR_PITCH_PLACEHOLDER}
            rows={4}
            rules={{
              required: 'Please type an elevator pitch',
            }}
          />
        </InputFormContainer>

        <ModalActions>
          <Button variant="white" rounded="regular" size="base" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="primary" rounded="regular" size="base" type="submit">
            Save
          </Button>
        </ModalActions>
      </Stack>
    </Modal>
  );
};
